import { PropsWithChildren, useContext, useEffect, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { Loading, PrintTransactionModal, TableHeader } from "components"
import { convertNumber, useCountryById, useDataCoaByCode, useEmployee, useHandleAuth, useLocation, useProvinceById, useRegencyById, useSubdistrictById } from "utils"
import { useSubdomainSetting } from "pages/Purchase/Transaction/PurchaseOrder/utils"
import { useSupplier } from "pages/Purchase/Transaction/components/Supplier/utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { checkQtyStatus, FormType } from "../utils"
import moment from "moment"
import { DataContext } from "../contexts"
import { TbCheck, TbX } from "react-icons/tb"

export function PrintBuildItem(params: { toggle: () => void }): JSX.Element {
  const { control } = useFormContext<FormType>()

  const transaction_date = useWatch({ control, name: "transaction_date" })
  const trx_code = useWatch({ control, name: "trx_code" })
  const qty_build_unit = useWatch({ control, name: "qty_build_unit" })
  const total_cac = useWatch({ control, name: "total_cac" })
  const total_amount = useWatch({ control, name: "total_amount"})
  const overhead = useWatch({ control, name: "overhead"})
  const location_id = useWatch({ control, name: "location_id"})
  const employee_id = useWatch({ control, name: "employee_id"})

  const { ingredient, product } = useContext(DataContext)

  const setting = useSubdomainSetting()
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const subdistrict = useSubdistrictById()
  const supplier = useSupplier()
  const dataLocation = useLocation()
  const dataEmployee = useEmployee()
  const { dataCoa } = useDataCoaByCode("LA13")
  
  const { userTokenData } = useHandleAuth()
  const userToken = userTokenData()

  const [address, setAddress] = useState<string>("")

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) {
      generateAddress += ", " + subdistrict.data.subdistrict_name
    }

    if (regency.data) {
      generateAddress += ", " + regency.data.regency_name
    }

    if (province.data) {
      generateAddress += ", " + province.data.province_name
    }

    if (country.data) {
      generateAddress += ", " + country.data.country_name
    }

    return generateAddress
  }

  const isEmpty = <tr><td colSpan={8}><Loading errorText="No data available" /></td></tr>

  return (
    <PrintTransactionModal
      isLandscape
      isLoading={company.isLoading || supplier.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading}
      toggle={params.toggle}
    >
      <section className="flex flex-col gap-3 text-[14px]">
        <div className="text-center text-2xl font-bold uppercase">
          {userToken?.company_name}
        </div>

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <InfoItems width={250} uppercase label="ADDRESS" text={getAddress()} />
                <InfoItems width={250} label="PHONE" text={setting.data?.phone!} />
                <InfoItems width={250} label="EMAIL" text={setting.data?.email!} />
                <InfoItems width={250} label="WEBSITE" text={setting.data?.website!} />
              </tbody>
            </table>
          </section>
 
          <section className="flex flex-col justify-between items-end">
            <div className="text-2xl font-bold text-right">
              BUILD ITEM
            </div>

            <table>
              <tbody className="font-bold ">
                <InfoItems width={280} textWidth={200} textRight label="DATE" text={moment(transaction_date).format("DD/MM/YYYY")} />
                <InfoItems width={280} textWidth={200} textRight label="BUILD ITEM NO" text={trx_code!} />
              </tbody>
            </table>
          </section>
        </section>

        <hr />

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <InfoItems label="CODE" width={250} text={product.product_barcode} />
                <InfoItems uppercase label="DESCRIPTION" width={250} text={product.product_name} />
                <InfoItems uppercase label="UNIT" width={250} text={product.mr_unit_name} />
                <InfoItems uppercase label="COMPONENT AT COST" width={250} text={convertNumber(Number(product.component_at_cost??0)).intoNormalAmount} />
                <InfoItems uppercase label="ACC OVERHEAD PRODUCTION" width={250} text={`${dataCoa?.coa_code} ${dataCoa?.coa_name}`} />
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <table className="table-top">
              <tbody className="font-bold ">
                <InfoItems width={280} textWidth={200} textRight label="LOCATION" text={dataLocation?.data?.find((item) => item?.value === location_id)?.label || "-"} />
                <InfoItems width={280} textWidth={200} textRight label="QTY BUILD" text={qty_build_unit ? convertNumber(qty_build_unit).intoNormalAmount : "0"} />
                <InfoItems width={280} textWidth={200} textRight label="EMPLOYEE" text={dataEmployee?.dataEmployee?.find((item) => item?.value === employee_id)?.label || "-"} />
              </tbody>
            </table>
          </section>
        </section>

        <table className="table table-bordered">
          <TableHeader>
            <tr>
              <th>NO</th>
              <th>CODE</th>
              <th>DESCRIPTION</th>
              <th>UNIT</th>
              <th>SOH</th>
              <th>QTY</th>
              <th>BUILD</th>
              <th>AVERAGE</th>
              <th>AMOUNT</th>
              
              <th>#</th>
            </tr>
          </TableHeader>
  
          <tbody>
            {!ingredient.length ? isEmpty : ingredient.map((item, key) => {
              const qty: number = item.qty * qty_build_unit
              const amount: number = qty * Number(item.avg_price)
  
              return (
                <tr key={key}>
                  <td className="text-center">{key + 1}</td>
                  <td className="text-center">{item.product_barcode}</td>
                  <td>{item.product_name}</td>
                  <td>{item.mr_unit_name}</td>
                  <td className="text-right">{convertNumber(item.soh).intoCurrency}</td>
                  <td className="text-right">{convertNumber(item.qty).intoCurrency}</td>
                  <td className="text-right">{convertNumber(qty).intoCurrency}</td>
                  <td className="text-right">{convertNumber(Number(item.avg_price)).intoCurrency}</td>
                  <td className="text-right">{convertNumber(amount).intoCurrency}</td>
                  <td>
                    <section className="flex justify-center">
                      {checkQtyStatus(item.qty, qty_build_unit, item.soh, Number(item.avg_price)) 
                        ? <TbCheck color="green" />
                        : <TbX color="red" />
                      }
                    </section>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <section className="grid grid-cols-2 items-stretch gap-1 mt-3">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <InfoItems width={200} uppercase label="STATUS APPROVE" text={product?.approve_status} />
                <InfoItems width={200} label="APPROVED BY" text={product?.approve_by_name || "-"} />
              </tbody>
            </table>
          </section>
 
          <section className="flex flex-col justify-between items-end">
            <table>
              <tbody className="font-bold ">
                <InfoItems width={200} textWidth={200} textRight label="TOTAL CAC" text={convertNumber(total_cac).intoCurrency} />
                <InfoItems width={200} textWidth={200} textRight label="TOTAL AMOUNT" text={convertNumber(total_amount).intoCurrency} />
                <InfoItems width={200} textWidth={200} textRight label="OVERHEAD" text={convertNumber(overhead).intoCurrency} />
              </tbody>
            </table>
          </section>
        </section>
      </section>
    </PrintTransactionModal>
  )
}

type InfoItemsType = {
  label: string
  text: string | number
  uppercase?: boolean
  textRight?: boolean
  width?: number
  textWidth?: number
}
const InfoItems = (props: PropsWithChildren<InfoItemsType>) => (
  <tr>
    <td width={props?.width ? props?.width : 120} className="pr-2 uppercase flex justify-between">
      <div>{props?.label}</div>
      <div>:</div>
    </td>
    <td width={props?.textWidth} className={props?.uppercase ? `uppercase` : props?.textRight ? "text-right" : ""}>{props?.text || "-"}</td>
  </tr>
)